import { sanitize } from "../../../../common-deprecated/utils/sanitizer";
import { formatPriceIntl } from "../../../../common-deprecated/priceUtils";
import type { CommonSettingsType } from "../../../../common-deprecated/settings/fetchCommonSettings";
import type { EngineType } from "../../../../common-deprecated/types/CommonTypes";
import type { SortedSpecType, SpecDataType } from "../../../../common-deprecated/types/SpecTypes";
import type { CarMotorizationType } from "../../../shared-logic/types/CarType";
import {
    type EcoConsumptionAreaAceType,
    type EcoConsumptionAreaType,
    type EcoConsumptionCostType,
    type EcoConsumptionSectionConfigEntryAceType,
    type EcoConsumptionSectionType,
    type EcoConsumptionSpecType,
    type EcoConsumptionSpecificationAceType,
    EcoConsumptionAreaEnum,
    EcoConsumptionSectionEnum,
} from "../types/EcoConsumptionType";

const enrichAceContent = (
    label: string = "",
    params: Partial<Record<keyof EcoConsumptionCostType, string | number>>,
    showDictionaryKeys: boolean,
): string => {
    if (showDictionaryKeys || !params) return label;

    const regex = /\{([^}]+)\}/g; // Regex to find all curly brace enclosed strings
    const replace = (source: string, replacements: { [name: string]: string | number }): string => {
        return source.replace(regex, (match: string) => {
            return `${replacements[match.substring(1, match.length - 1)]}`;
        });
    };
    return replace(label, params);
};

export const findConfigurationByEngineType = (
    aceConfigurations: EcoConsumptionSectionConfigEntryAceType[],
    engine: EngineType | CarMotorizationType["engine"],
): EcoConsumptionSectionConfigEntryAceType | undefined => {
    if (engine.fuelCell) return aceConfigurations.find((config) => config.type === EcoConsumptionSectionEnum.Hydrogen);
    if (engine.plugin) return aceConfigurations.find((config) => config.type === EcoConsumptionSectionEnum.PlugIn);
    if (engine.hybrid) return aceConfigurations.find((config) => config.type === EcoConsumptionSectionEnum.Hybrid);
    if (engine.electric) return aceConfigurations.find((config) => config.type === EcoConsumptionSectionEnum.Electric);
    // TypeCode 2 is Diesel, TypeCode 1 is Petrol
    if (engine.typeCode === "2")
        return aceConfigurations.find((config) => config.type === EcoConsumptionSectionEnum.Diesel);
    return aceConfigurations.find((config) => config.type === EcoConsumptionSectionEnum.Petrol);
};

const formatCostsPrice = (
    value: number | string | undefined,
    commonSettings: CommonSettingsType,
    intlOptions: Intl.NumberFormatOptions = {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        currencyDisplay: "symbol",
    },
): string | undefined => {
    if (value === undefined) return undefined;
    return formatPriceIntl(commonSettings, value, intlOptions, false, false).primaryPrice;
};

const findSpec = (techSpecs: SpecDataType[], key: string): SpecDataType | null => {
    return (
        techSpecs.find(({ internalCode, linkedSpec }) => {
            return internalCode.toLowerCase() === key || (linkedSpec && linkedSpec?.toLowerCase() === key);
        }) || null
    );
};

/**
 * Processes a list of technical specifications and matches them with a list of specification keys.
 * Returns an array of matched specifications or null if no match is found.
 * @param techSpecs An array of SpecDataType objects representing the technical specifications.
 * @param specs An array of objects containing key and optional fallbacks to match against techSpecs.
 * @returns An array of SpecDataType objects or null values, corresponding to the matched specifications from techSpecs.
 */
const formatAceSpecs = (
    techSpecs: SpecDataType[],
    specs: EcoConsumptionSpecificationAceType["data"],
): EcoConsumptionSpecType["data"] => {
    const collector = specs.map((spec) => {
        const techSpec =
            findSpec(techSpecs, spec.key) ||
            spec.fallbacks?.map((fallback) => findSpec(techSpecs, fallback)).find(Boolean);
        return techSpec || null;
    });
    return collector;
};

const formatAceAreas = (
    areas: EcoConsumptionAreaAceType[],
    techSpecs: SpecDataType[],
    costs: EcoConsumptionCostType | null,
    commonSettings: CommonSettingsType,
): EcoConsumptionAreaType[] => {
    const disclaimers: SanitizedString[] = [];
    const processedAreas: EcoConsumptionAreaType[] = areas
        .map((area) => {
            switch (area.type) {
                case EcoConsumptionAreaEnum.FuelArea: {
                    // Cast to const because .find never allows default value
                    const primaryValue = findSpec(techSpecs, area.primaryValue || "");
                    const secondaryValue = findSpec(techSpecs, area.secondaryValue || "");
                    return {
                        type: area.type,
                        title: area.title,
                        subtitle: area.subtitle,
                        primary: {
                            label: area.primaryLabel,
                            data: primaryValue || area.primaryValueFallback,
                        },
                        secondary: {
                            label: area.secondaryLabel,
                            data: secondaryValue || area.secondaryValueFallback,
                        },
                    };
                }
                case EcoConsumptionAreaEnum.SpecArea: {
                    return {
                        type: area.type,
                        title: area.title,
                        subtitle: area.subtitle,
                        specifications: (area.specifications || []).map((spec) => {
                            if (spec.disclaimer) disclaimers.push(spec.disclaimer);
                            return {
                                label: spec.label,
                                data: formatAceSpecs(techSpecs, spec.data),
                                highlighted: spec.highlighted,
                                disclaimer: spec.disclaimer,
                            };
                        }),
                    };
                }
                case EcoConsumptionAreaEnum.EmissionArea: {
                    return {
                        type: area.type,
                        title: area.title,
                        subtitle: area.subtitle,
                        emissionEntries: (area.emissionEntries || []).map((emission) => {
                            return {
                                label: emission.label,
                                value: findSpec(techSpecs, emission.value.toLowerCase())?.value,
                            };
                        }),
                    };
                }
                case EcoConsumptionAreaEnum.CostsArea: {
                    const showDictionaryKeys = !!commonSettings.query.showDictionaryKeys;
                    if (area.co2CostsLongTermDisclaimer)
                        disclaimers.push(
                            sanitize(
                                enrichAceContent(
                                    area.co2CostsLongTermDisclaimer,
                                    {
                                        disclaimerFrom: costs?.disclaimerFrom,
                                        disclaimerTo: costs?.disclaimerTo,
                                    },
                                    showDictionaryKeys,
                                ),
                            ),
                        );
                    if (area.totalDisclaimer)
                        disclaimers.push(
                            sanitize(
                                enrichAceContent(
                                    area.totalDisclaimer,
                                    {
                                        disclaimerFrom: costs?.disclaimerFrom,
                                        disclaimerTo: costs?.disclaimerTo,
                                    },
                                    showDictionaryKeys,
                                ),
                            ),
                        );
                    return {
                        type: area.type,
                        title: area.title,
                        subtitle: area.subtitle,
                        yearlyMilageEnergyCosts: {
                            title: area.yearlyMilageEnergyCostsTitle || "",
                            subtitle: enrichAceContent(
                                area.yearlyMilageEnergyCostsSubTitle,
                                {
                                    ...(costs?.fuelUnitPriceEuros && {
                                        fuelUnitPriceEuros: formatCostsPrice(costs.fuelUnitPriceEuros, commonSettings, {
                                            minimumFractionDigits: 3,
                                            maximumFractionDigits: 3,
                                            currencyDisplay: "symbol",
                                        }),
                                    }),
                                    ...(costs?.electricityKwhPriceCents && {
                                        electricityKwhPriceCents: formatCostsPrice(
                                            costs.electricityKwhPriceCents,
                                            commonSettings,
                                            {
                                                style: "decimal",
                                                currency: undefined,
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 3,
                                                currencyDisplay: "symbol",
                                            },
                                        ),
                                    }),
                                    pricesForYear: costs?.pricesForYear,
                                },
                                showDictionaryKeys,
                            ),
                            highlighted: true,
                            price: formatCostsPrice(costs?.yearlyCostEuros, commonSettings),
                        },
                        co2Costs: [
                            {
                                title: area.co2CostsLongTermTitle || "",
                                disclaimer: sanitize(
                                    enrichAceContent(
                                        area.co2CostsLongTermDisclaimer,
                                        {
                                            disclaimerFrom: costs?.disclaimerFrom,
                                            disclaimerTo: costs?.disclaimerTo,
                                        },
                                        showDictionaryKeys,
                                    ),
                                ),
                                highlighted: true,
                            },
                            ...(costs?.co2CostMedium !== undefined
                                ? [
                                      {
                                          title: enrichAceContent(
                                              area.co2CostsByMedianPriceTitle,
                                              {
                                                  co2TonPriceMedium: formatCostsPrice(
                                                      costs.co2TonPriceMedium,
                                                      commonSettings,
                                                  ),
                                              },
                                              showDictionaryKeys,
                                          ),
                                          highlighted: true,
                                          price: formatCostsPrice(costs?.co2CostMedium, commonSettings),
                                      },
                                  ]
                                : []),
                            {
                                title: enrichAceContent(
                                    area.co2CostsByLowPriceTitle,
                                    {
                                        co2TonPriceLow: formatCostsPrice(costs?.co2TonPriceLow, commonSettings),
                                    },
                                    showDictionaryKeys,
                                ),
                                highlighted: false,
                                price: formatCostsPrice(costs?.co2CostLow, commonSettings),
                            },
                            {
                                title: enrichAceContent(
                                    area.co2CostsByHighPriceTitle,
                                    {
                                        co2TonPriceHigh: formatCostsPrice(costs?.co2TonPriceHigh, commonSettings),
                                    },
                                    showDictionaryKeys,
                                ),
                                highlighted: false,
                                price: formatCostsPrice(costs?.co2CostHigh, commonSettings),
                            },
                        ],
                        total: {
                            title: area.totalLabel || "",
                            highlighted: true,
                            price: formatCostsPrice(costs?.taxYearly, commonSettings),
                            disclaimer: sanitize(
                                enrichAceContent(
                                    area.totalDisclaimer,
                                    {
                                        disclaimerFrom: costs?.disclaimerFrom,
                                        disclaimerTo: costs?.disclaimerTo,
                                    },
                                    showDictionaryKeys,
                                ),
                            ),
                            taxExempt: area.totalPriceOverride || "",
                        },
                    };
                }
                case EcoConsumptionAreaEnum.DisclaimerArea: {
                    return {
                        type: area.type,
                        title: area.title,
                        subtitle: area.subtitle,
                        generalDisclaimer: area.generalDisclaimer ? sanitize(area.generalDisclaimer) : "",
                        disclaimers: [],
                    };
                }
                default:
                    return null as unknown as EcoConsumptionAreaType;
            }
        })
        .filter(Boolean)
        .map((area) => {
            if (area.type === EcoConsumptionAreaEnum.DisclaimerArea) {
                area.disclaimers = disclaimers;
            }
            return area;
        });

    return processedAreas;
};

export const formatEcoConsumptionConfiguration = (
    commonSettings: CommonSettingsType,
    sectionConfiguration: EcoConsumptionSectionConfigEntryAceType | undefined,
    techSpecs: SortedSpecType[],
    costs: EcoConsumptionCostType | null,
): EcoConsumptionSectionType | null => {
    if (!sectionConfiguration) return null;
    const flattenedTechSpecs = techSpecs.flatMap((spec) => spec.data as SpecDataType[]).filter(Boolean);
    return {
        title: sectionConfiguration.title,
        areas: formatAceAreas(sectionConfiguration.areas || [], flattenedTechSpecs, costs, commonSettings),
        type: sectionConfiguration.type || EcoConsumptionSectionEnum.Petrol,
    };
};
